import { FC, MouseEvent, useMemo, useState } from 'react';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { ArrowUpRight, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LoadingSpinner } from '@/components/loading-spinner';
import { useNotification } from '@/components/notifications';
import { useGetNotifications } from '@/hooks/queries/use-get-notifications';
import { useMarkNotificationRead } from '@/hooks/mutations/use-mark-notification-read';
import {
  ReviewAccessRequestError,
  useReviewAccessRequest,
} from '@/hooks/mutations/use-review-access-request';
import { useOrgStore } from '@/stores/org-store';
import { groupNotificationsByTime } from './helpers/group-notifications';
import { resolveNotificationTarget } from './helpers/get-notification-target';
import {
  getTriggerBatchItems,
  TriggerBatchItem,
} from './helpers/get-trigger-batch-items';
import { useOpenNotification } from './use-open-notification';
import {
  NotificationEventType,
  NotificationItem,
} from '@/types/notification-types';
import { AccessRequestDecision } from '@/types/share-types';

const DECISION_LABEL: Record<AccessRequestDecision, string> = {
  approved: 'Access granted',
  denied: 'Request denied',
};

const formatTimestamp = (iso: string): string => {
  const date = parseISO(iso);
  const time = format(date, 'h:mm a');
  if (isToday(date)) return `Today, ${time}`;
  if (isYesterday(date)) return `Yesterday, ${time}`;
  return format(date, 'MMM d, h:mm a');
};

// Label for a target that is not a single run; a run is always "View run".
const LINK_LABELS: Partial<Record<NotificationEventType, string>> = {
  workflow_success: 'View run',
  workflow_failure: 'View run',
  trigger_runs_started: 'View runs',
};

const getLinkLabel = (notification: NotificationItem): string | null => {
  const target = resolveNotificationTarget(notification);
  if (!target) return null;
  if (target.kind === 'run') return 'View run';
  return LINK_LABELS[notification.event_type] ?? 'Open workflow';
};

const BATCH_PREVIEW_COUNT = 3;

interface TriggerBatchRowProps {
  item: TriggerBatchItem;
  onOpen: (target: string) => void;
}

const TriggerBatchRow: FC<TriggerBatchRowProps> = ({ item, onOpen }) => {
  const { target } = item;
  return (
    <li className='flex min-w-0 items-center gap-2 text-sm'>
      {target ? (
        <button
          type='button'
          onClick={() => onOpen(target)}
          className='truncate text-left text-foreground underline-offset-2 hover:underline'
        >
          {item.label}
        </button>
      ) : (
        <span className='truncate text-foreground'>{item.label}</span>
      )}
      {item.detail && (
        <span className='shrink-0 text-muted-foreground'>{item.detail}</span>
      )}
    </li>
  );
};

interface TriggerBatchListProps {
  notification: NotificationItem;
  items: TriggerBatchItem[];
}

const TriggerBatchList: FC<TriggerBatchListProps> = ({
  notification,
  items,
}) => {
  const [expanded, setExpanded] = useState(false);
  const openNotification = useOpenNotification();

  const visible = expanded ? items : items.slice(0, BATCH_PREVIEW_COUNT);
  const hiddenCount = items.length - visible.length;

  const handleOpen = (target: string) => openNotification(notification, target);

  return (
    <ul className='flex flex-col gap-1'>
      {visible.map((item) => (
        <TriggerBatchRow key={item.key} item={item} onOpen={handleOpen} />
      ))}
      {hiddenCount > 0 && (
        <li>
          <button
            type='button'
            onClick={() => setExpanded(true)}
            className='text-sm text-muted-foreground hover:text-foreground'
          >
            Show {hiddenCount} more
          </button>
        </li>
      )}
    </ul>
  );
};

const AccessRequestActions: FC<{ notification: NotificationItem }> = ({
  notification,
}) => {
  const currentOrg = useOrgStore((s) => s.currentOrg);
  const review = useReviewAccessRequest();
  const markRead = useMarkNotificationRead();
  const notify = useNotification();
  const [result, setResult] = useState<AccessRequestDecision | null>(null);

  const workflowId = notification.metadata?.workflow_id as string | undefined;
  const requestId = notification.metadata?.request_id as string | undefined;
  const orgId = currentOrg?.orgid;

  if (!workflowId || !requestId || !orgId) return null;

  const isResolved = result !== null || Boolean(notification.resolved_at);

  if (isResolved) {
    const outcome =
      result ??
      (notification.metadata?.resolution as AccessRequestDecision | undefined);
    const label = outcome ? DECISION_LABEL[outcome] : 'Request resolved';
    return <span className='text-sm text-muted-foreground'>{label}</span>;
  }

  const handle = (decision: AccessRequestDecision) => {
    review.mutate(
      { orgId, workflowId, requestId, decision },
      {
        onSuccess: () => {
          setResult(decision);
          markRead.mutate(notification.id);
          notify.success(DECISION_LABEL[decision]);
        },
        onError: (error) => {
          if (
            error instanceof ReviewAccessRequestError &&
            error.statusCode === 409
          ) {
            notify.info('This request has already been resolved.');
          } else {
            notify.error('Failed to review request');
          }
        },
      },
    );
  };

  return (
    <div className='flex shrink-0 items-center gap-2'>
      <Button
        size='sm'
        onClick={() => handle('approved')}
        disabled={review.isPending}
      >
        Approve
      </Button>
      <Button
        size='sm'
        variant='outline'
        onClick={() => handle('denied')}
        disabled={review.isPending}
      >
        Deny
      </Button>
    </div>
  );
};

interface NotificationCardProps {
  notification: NotificationItem;
  onSelect: (notification: NotificationItem) => void;
}

const NotificationCard: FC<NotificationCardProps> = ({
  notification,
  onSelect,
}) => {
  const markRead = useMarkNotificationRead();
  const isUnread = !notification.read_at;
  const isAccessRequest = notification.event_type === 'access_requested';
  const linkLabel = getLinkLabel(notification);
  const batchItems = useMemo(
    () => getTriggerBatchItems(notification),
    [notification],
  );

  const handleMarkRead = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    markRead.mutate(notification.id);
  };

  return (
    <div className='group flex w-full flex-col gap-3 rounded-lg bg-accent p-4'>
      <div className='flex items-start gap-2'>
        <button
          type='button'
          onClick={() => onSelect(notification)}
          className='flex min-w-0 flex-1 flex-col gap-1 text-left'
        >
          <span className='truncate text-sm text-muted-foreground'>
            {notification.title}
          </span>
          {notification.body && (
            <p className='text-base text-foreground'>{notification.body}</p>
          )}
        </button>
        {isUnread && (
          <div className='flex shrink-0 items-center'>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  onClick={handleMarkRead}
                  disabled={markRead.isPending}
                  aria-label='Mark as read'
                  className='flex size-5 items-center justify-center rounded-full border border-transparent transition-colors hover:border-border disabled:opacity-50'
                >
                  <span className='size-2 rounded-full bg-primary' />
                </button>
              </TooltipTrigger>
              <TooltipContent side='left'>Mark as read</TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
      {batchItems.length > 0 && (
        <TriggerBatchList notification={notification} items={batchItems} />
      )}
      <div className='flex items-center justify-between gap-2'>
        <span className='text-sm text-muted-foreground'>
          {formatTimestamp(notification.created_at)}
        </span>
        {isAccessRequest ? (
          <AccessRequestActions notification={notification} />
        ) : (
          linkLabel && (
            <Button
              variant='link'
              onClick={() => onSelect(notification)}
              className='pointer-events-none h-auto w-fit shrink-0 gap-1 p-0 text-sm opacity-0 transition-opacity focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100'
            >
              {linkLabel}
              <ArrowUpRight className='size-3.5' />
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export const ActivityFeed: FC = () => {
  const { data, isLoading, isError } = useGetNotifications();
  const handleSelect = useOpenNotification();

  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const groups = useMemo(() => groupNotificationsByTime(items), [items]);

  if (isLoading) {
    return (
      <div className='flex flex-1 items-center justify-center'>
        <LoadingSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground'>
        Unable to load notifications.
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className='flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center'>
        <Bell className='size-8 text-muted-foreground' />
        <p className='text-sm text-muted-foreground'>
          You&apos;re all caught up.
        </p>
      </div>
    );
  }

  return (
    <div className='flex flex-1 flex-col gap-12 overflow-y-auto p-6 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:bg-transparent [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-corner]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/50 [scrollbar-width:thin] [scrollbar-color:rgba(128,128,128,0.3)_transparent]'>
      {groups.map((group) => (
        <div key={group.key} className='flex flex-col gap-2'>
          <h4 className='text-sm font-medium text-muted-foreground'>
            {group.label}
          </h4>
          <div className='flex flex-col gap-2'>
            {group.items.map((item) => (
              <NotificationCard
                key={item.id}
                notification={item}
                onSelect={handleSelect}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
